import { useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Registers the device for FCM push notifications via the
 * `@capacitor-firebase/messaging` plugin (which natively bridges APNs↔FCM on
 * iOS so the backend's firebase-admin SDK can target the device).
 *
 * Gracefully no-ops on:
 *   - the web platform (Capacitor.isNativePlatform() === false)
 *   - any build where the plugin isn't installed yet (dynamic import fails)
 *
 * On login  → requests permission, fetches an FCM token, POSTs it to
 *             /api/device-tokens. Also subscribes to `tokenReceived` so we
 *             upsert on rotation.
 * On logout → DELETEs the most recent token from the backend.
 */
export default function PushRegistrar() {
  const { user } = useAuth();
  const userId = user?.id || null;

  useEffect(() => {
    if (!userId) return undefined;
    let lastToken = null;
    let listeners = [];
    let cancelled = false;

    (async () => {
      let Capacitor;
      let FirebaseMessaging;
      try {
        ({ Capacitor } = await import("@capacitor/core"));
      } catch {
        return; // Capacitor not available — running in a plain web context
      }
      if (!Capacitor?.isNativePlatform?.()) return;
      try {
        ({ FirebaseMessaging } = await import(
          "@capacitor-firebase/messaging"
        ));
      } catch {
        // Plugin not installed in this build (web preview before native sync).
        return;
      }
      const platform = (Capacitor.getPlatform?.() || "web").toLowerCase();
      // Surface which backend this build is hitting — invaluable for debugging
      // "delivered to 0 native devices" issues across preview vs production.
      try {
        const base =
          (await import("@/lib/api")).API_BASE || "(unknown)";
        console.log(`[PushRegistrar] platform=${platform} backend=${base}`);
      } catch {
        /* no-op */
      }

      const sendToken = async (token) => {
        if (!token || cancelled || token === lastToken) return;
        lastToken = token;
        try {
          const res = await api.post("/device-tokens", { token, platform });
          console.log(
            `[PushRegistrar] POST /device-tokens OK status=${res.status}`,
          );
        } catch (err) {
          console.error(
            `[PushRegistrar] POST /device-tokens FAILED status=${err?.response?.status} msg=${
              err?.response?.data?.detail || err?.message
            }`,
          );
        }
      };

      // Listen for token rotations.
      try {
        listeners.push(
          await FirebaseMessaging.addListener("tokenReceived", (ev) => {
            sendToken(ev?.token);
          }),
        );
        listeners.push(
          await FirebaseMessaging.addListener(
            "notificationActionPerformed",
            () => {
              try {
                window.location.assign("/inbox");
              } catch {
                /* no-op */
              }
            },
          ),
        );
      } catch {
        /* listener wiring is best-effort */
      }

      // Request permission, then grab the initial FCM token.
      try {
        const perm = await FirebaseMessaging.requestPermissions();
        if (perm?.receive !== "granted") return;
        const { token } = await FirebaseMessaging.getToken();
        await sendToken(token);
      } catch {
        /* swallow — token may arrive via the listener after APNs settles */
      }
    })();

    return () => {
      cancelled = true;
      listeners.forEach((l) => l?.remove?.());
      if (lastToken) {
        api.delete(`/device-tokens/${encodeURIComponent(lastToken)}`).catch(() => {});
      }
    };
  }, [userId]);

  return null;
}
